import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import type { MediaProvider, MediaOptions, EditorActions, EditorProps } from '@atlaskit/editor-core';
import { CollapsedEditor, Editor as FabricEditor, EditorContext, WithEditorActions } from '@atlaskit/editor-core';
import { SmartCardProvider } from '@atlaskit/link-provider';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { WithUploadCredentials } from 'view/common/with-upload-credentials';
import { PortalAutoformattingProvider } from 'view/common/auto-formatting-provider';
import { PortalCardClient } from 'view/common/smart-card-client';
import { PortalCardProvider } from 'view/common/smart-card-provider';
import { getEmojiProvider } from 'view/common/emoji-provider';
import ErrorBoundaryMessage from '../../error-boundary-message';
import { withBrowserMetricSubmitter } from 'view/browser-metric';
import { BrowserMetricViewName } from 'view/common/constants';
import type { FieldConfigs } from '../../mention-provider';
import { getMentionProvider } from '../../mention-provider';
import { EditorWrapper } from './styled';
import type { LooselyLoadableLoadingComponentProps } from '../../loosely-loadable-wrapper';

export interface MediaContext {
    portalId: number;
    requestId: number;
}

export interface RichTextEditorActions {
    clear: () => void;
}

type RichTextEditorType = 'comment-editor' | 'field';

type EditorView = Parameters<Required<EditorProps>['onSave']>[0];

export interface RichTextEditorProps {
    editorType: RichTextEditorType;
    placeholderWhenCollapsed?: string;
    placeholderWhenExpanded?: string;
    isExpanded: boolean;
    disabled: boolean;
    isEmojiEnabled: boolean;
    defaultValue?: string;
    expandEditor?: () => void;
    onSave?: (comment: JSONDocNode, actions: RichTextEditorActions) => void;
    onChange?: (comment: JSONDocNode) => void;
    onCancel?: (actions: RichTextEditorActions) => void;
    mediaContext?: MediaContext;
    fabricEmojiUrl?: string;
    /** Used to display when the editor is loading locale translation messages */
    LoadingComponent?: React.ComponentType<LooselyLoadableLoadingComponentProps>;
    requestKey?: string;
    accountId?: string;
    shouldFocus?: boolean;
    fieldConfigs?: FieldConfigs;
}

interface RichTextEditorState {
    editorMessages: { [key: string]: string };
}

const autoformattingProvider = new PortalAutoformattingProvider();
const smartCardClient = new PortalCardClient();
const cardProvider = new PortalCardProvider();

export class RichTextEditorV2 extends React.Component<RichTextEditorProps & InjectedIntlProps, RichTextEditorState> {
    static defaultProps = {
        LoadingComponent: () => null,
    };

    transformer = new JSONTransformer();
    editorActions?: EditorActions = undefined;
    mentionProvider = this.props.accountId
        ? Promise.resolve(getMentionProvider(this.props.accountId, this.props.requestKey, this.props.fieldConfigs))
        : undefined;

    componentDidMount() {
        this.props.shouldFocus && this.focusOnEditor();
    }

    componentDidUpdate(prevProps: RichTextEditorProps & InjectedIntlProps) {
        if (!prevProps.shouldFocus && this.props.shouldFocus) {
            this.focusOnEditor();
        }
    }

    focusOnEditor = () => {
        //we need to use the setTimeout here, otherwise focusing on the editor immediately will cause the caret to flash
        //50ms seem like our magic number, but it might varies on browsers
        setTimeout(() => {
            this.editorActions && this.editorActions.focus();
        }, 50);
    };

    onSave = (editorView: EditorView, actions: RichTextEditorActions) => {
        const { onSave } = this.props;
        if (onSave) {
            onSave(this.transformer.encode(editorView.state.doc), actions);
        }
    };

    renderEditorWithMediaContext = (mediaContext: MediaContext) => {
        const { portalId, requestId } = mediaContext;

        return (
            <WithUploadCredentials portalId={portalId} requestId={requestId}>
                {(provider) => this.renderEditorWithMediaProvider(provider)}
            </WithUploadCredentials>
        );
    };

    renderEditorWithMediaProvider = (mediaProvider: Promise<MediaProvider>) => {
        const mediaOptions = {
            provider: mediaProvider,
            allowMediaSingle: true,
        };
        return this.renderEditor(mediaOptions);
    };

    internalOnChange = (editorView: EditorView) => {
        const { onChange } = this.props;
        if (onChange) {
            onChange(this.transformer.encode(editorView.state.doc));
        }
    };

    renderEditor = (mediaOptions?: MediaOptions) => {
        const renderFunction = (actions: EditorActions) => this.internalRenderEditor(actions, mediaOptions);
        return (
            <EditorContext>
                {/* eslint-disable-next-line react/jsx-no-bind */}
                <WithEditorActions render={renderFunction} />
            </EditorContext>
        );
    };

    internalOnCancel = () => {
        if (this.editorActions) {
            this.props.onCancel?.(this.editorActions);
        }
    };

    internalOnSave = (editorView: EditorView) => {
        if (this.editorActions) {
            this.onSave(editorView, this.editorActions);
        }
    };

    internalRenderEditor = (actions: EditorActions, mediaOptions?: MediaOptions) => {
        const {
            placeholderWhenCollapsed,
            isExpanded,
            expandEditor,
            placeholderWhenExpanded,
            onCancel,
            disabled,
            isEmojiEnabled,
            defaultValue,
            fabricEmojiUrl,
            onSave,
            editorType,
        } = this.props;

        const internalOnCancel = onCancel === undefined ? undefined : () => this.internalOnCancel();

        const internalOnSave =
            onSave === undefined ? undefined : (editorView: EditorView) => this.internalOnSave(editorView);

        /**
         * When the editor is rendered as a comment box, the entered comment is stored in the Redux store as UI state when a comment is submitted.
         * If the REST call to save the comment succeeds, then it's cleared, otherwise it's used to render what the user had entered so they can retry.
         */
        if (editorType === 'comment-editor') {
            if (defaultValue) {
                actions.replaceDocument(defaultValue);
            } else {
                actions.clear();
            }
        }

        this.editorActions = actions;

        const tableConfig = {
            allowColumnResizing: true,
            allowControls: true,
            allowHeaderRow: true,
            allowHeaderColumn: true,
            allowNumberColumn: true,
        };

        return (
            <EditorWrapper>
                <SmartCardProvider client={smartCardClient}>
                    <CollapsedEditor
                        placeholder={placeholderWhenCollapsed}
                        isExpanded={isExpanded}
                        onFocus={expandEditor}
                    >
                        <FabricEditor
                            defaultValue={defaultValue}
                            appearance="comment"
                            placeholder={placeholderWhenExpanded}
                            // upgrading eslint-plugin-react to 7.31.11. Please correct when this code is revisited.
                            // eslint-disable-next-line react/jsx-no-bind
                            onSave={internalOnSave}
                            // upgrading eslint-plugin-react to 7.31.11. Please correct when this code is revisited.
                            // eslint-disable-next-line react/jsx-no-bind
                            onCancel={internalOnCancel}
                            onChange={this.internalOnChange}
                            allowTextColor
                            allowRule
                            allowPanel
                            allowTables={tableConfig}
                            allowDate={false}
                            UNSAFE_cards={{
                                provider: Promise.resolve(cardProvider),
                            }}
                            autoformattingProvider={Promise.resolve(autoformattingProvider)}
                            // @ts-ignore TS(2322) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Type 'Promise<import("/Users/marora/dev/jsd-portal... Remove this comment to see the full error message
                            emojiProvider={getEmojiProvider(isEmojiEnabled, fabricEmojiUrl)}
                            media={mediaOptions}
                            disabled={disabled}
                            waitForMediaUpload
                            mentionProvider={this.mentionProvider}
                            popupsBoundariesElement={document.documentElement}
                            // TODO: remove featureFlags param once its dependecy is removed from editor package
                            // https://product-fabric.atlassian.net/browse/ED-14471
                            featureFlags={{
                                codeBidiWarnings: true,
                            }}
                        />
                    </CollapsedEditor>
                </SmartCardProvider>
            </EditorWrapper>
        );
    };

    render() {
        const { editorType, mediaContext } = this.props;
        const editor = mediaContext ? this.renderEditorWithMediaContext(mediaContext) : this.renderEditor();

        return <ErrorBoundaryMessage type={editorType}>{editor}</ErrorBoundaryMessage>;
    }
}

export default withBrowserMetricSubmitter(BrowserMetricViewName.COMMENT_EDITOR_VIEW, injectIntl(RichTextEditorV2));
